import { Link } from '@usehatchapp/material';
import {
  Button,
  Form,
  ForwardedInput,
  Label,
  ToggleSwitch,
} from 'hatch-components';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useConnect } from 'redux-bundler-hook';
import DrawerBody from 'root/components/molecules/Drawer/components/DrawerBody';
import DrawerError from 'root/components/molecules/Drawer/components/DrawerError';
import DrawerFooter from 'root/components/molecules/Drawer/components/DrawerFooter';
import FormMaskedInput from 'root/components/molecules/FormMaskedInput';
import { FormSelect } from 'root/components/molecules/FormSelect';
import { StyledHelperText } from 'root/components/molecules/Select/styles';
import formatPhoneNumber from 'utils/formatPhoneNumber';
import { getEmailDomain } from 'utils/getEmailDomain';
import { timezoneOptions } from 'utils/getTimezone';
import isCorrectHatchIdFormat from 'utils/isCorrectHatchIdFormat';
import telephoneInputMask from 'utils/telephoneInputMask';

import {
  StyledObservesDaylightSavingsTimeLabel,
  StyledObservesDaylightSavingsTimeRow,
} from './styles';

const EditWorkspaceForm = ({ onClose, boardId, form }) => {
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [initialized, setInitialized] = useState(false);
  const {
    boardsById,
    currentOrganizationId,
    doCreateNotificationMessage,
    doUpdateBoard,
    freshCurrentBoardDetails,
    freshCurrentOrganizationBoards,
    freshCurrentOrganizationTeam,
    isUpdatingBoard,
    teamMemberIDsByBoard,
    twilioRegisteredBrandOptions,
    currentOrganization,
    teamMemberOptionsWithoutChatbot: teamMemberOptions,
    chatbotTeamMemberOptions,
  } = useConnect(
    'doCreateNotificationMessage',
    'doUpdateBoard',
    'selectBoardsById',
    'selectCurrentOrganizationId',
    'selectFreshCurrentBoardDetails',
    'selectFreshCurrentOrganizationBoards',
    'selectFreshCurrentOrganizationTeam',
    'selectIsUpdatingBoard',
    'selectTeamMemberIDsByBoard',
    'selectTwilioRegisteredBrandOptions',
    'selectCurrentOrganization',
    'selectTeamMemberOptionsWithoutChatbot',
    'selectChatbotTeamMemberOptions',
  );

  const {
    register,
    control,
    errors,
    handleSubmit,
    reset,
    formState,
    setValue,
    watch,
  } = form;
  const watchObservesDaylightSavings = watch('observesDaylightSavings');
  const { isDirty: formIsDirty } = formState;

  const disabled =
    !freshCurrentBoardDetails ||
    isUpdatingBoard ||
    !freshCurrentOrganizationTeam;

  const emailAlias = boardsById?.[boardId]?.workspaceGroup?.email;
  const emailDomain = getEmailDomain();

  // prefill form with values from store
  useEffect(() => {
    if (
      freshCurrentBoardDetails &&
      freshCurrentOrganizationBoards &&
      !initialized &&
      isCorrectHatchIdFormat(boardId)
    ) {
      const board = boardsById?.[boardId] || {};
      const { active, name, nickname } = board;
      const { forwardTo, timezone } = board?.settings || {};
      const { email, phoneNumber } = board?.workspaceGroup || {};
      const [freshEmailAlias] = email?.split('@') || [];

      if (!active) {
        return onClose({ ignore: true });
      }

      reset({
        email: freshEmailAlias,
        forwardTo: formatPhoneNumber(forwardTo),
        name,
        nickname,
        observesDaylightSavings: timezone?.observesDaylightSavings,
        phoneNumber: formatPhoneNumber(phoneNumber),
        teamMemberIDs: teamMemberOptions.filter((to) =>
          (teamMemberIDsByBoard?.[boardId] || []).includes(to.value),
        ),
        timezone:
          typeof timezone?.offsetHours === 'number'
            ? {
                label: timezoneOptions.find(
                  (t) => t.value === timezone?.offsetHours?.toString?.(),
                )?.label,
                value: timezone?.offsetHours?.toString?.(),
              }
            : '',
      });
      setInitialized(true);
    }
    return null;
  }, [
    freshCurrentBoardDetails,
    freshCurrentOrganizationBoards,
    freshCurrentOrganizationTeam,
    initialized,
    boardsById,
    boardId,
    teamMemberIDsByBoard,
    teamMemberOptions,
    emailAlias,
    reset,
    onClose,
  ]);

  const onDone = (error, { data } = {}) => {
    if (error) {
      // TODO: map errors to form
      return setSubmitErrorMessage(error?.message?.detail);
    }

    doCreateNotificationMessage({
      message: `'${data.name}' has been edited.`,
      type: 'success',
    });
    return onClose({ ignore: true });
  };

  const onSubmit = (data) => {
    if (isUpdatingBoard) return;

    const {
      name,
      nickname,
      email,
      teamMemberIDs,
      timezone,
      forwardTo,
      observesDaylightSavings,
    } = data;
    const workspaceId = boardsById[boardId].id;

    doUpdateBoard(
      {
        boardId,
        data: {
          email,
          forwardTo,
          name,
          nickname,
          teamMemberIDs: [
            ...(teamMemberIDs || []),
            // TODO: Move this chatbot adding logic to backend asap (UXP-375)
            ...chatbotTeamMemberOptions,
          ].map(({ value }) => value),
          timezone: {
            observesDaylightSavings,
            offsetHours: parseInt(timezone.value, 10),
          },
        },
        organizationId: currentOrganizationId,
        workspaceId,
      },
      onDone,
    );
  };

  const contactSupportUrl = 'mailto:support@usehatchapp.com';
  const complianceValue = useMemo(() => {
    const twilioRegistration = (
      currentOrganization.twilioRegistrations || []
    ).find((item) =>
      item.workspaces.find((workspace) => workspace.mongo_id === boardId),
    );

    return twilioRegistration
      ? twilioRegisteredBrandOptions.find(
          (item) => item.value === twilioRegistration.id,
        )
      : {};
  }, [
    twilioRegisteredBrandOptions,
    currentOrganization.twilioRegistrations,
    boardId,
  ]);

  useEffect(() => {
    if (twilioRegisteredBrandOptions.length === 1) {
      setValue('compliance10dlc', twilioRegisteredBrandOptions[0], {
        shouldDirty: false,
      });
    }
  }, [twilioRegisteredBrandOptions, setValue]);

  return (
    <>
      {submitErrorMessage ? <DrawerError message={submitErrorMessage} /> : null}
      <DrawerBody>
        <Form disabled={disabled} onSubmit={handleSubmit(onSubmit)}>
          <Label required htmlFor="10dlc">
            Company
          </Label>
          <FormSelect
            control={control}
            data-test-id="10dlc-brand"
            fullWidth
            id="10dlc"
            name="compliance10dlc"
            isDisabled
            options={twilioRegisteredBrandOptions}
            defaultValue={complianceValue}
            style={{ marginBottom: 0 }}
          />
          <StyledHelperText>
            You are unable to edit this field. Reach out to{' '}
            <Link data-test-id="contact-support-link" href={contactSupportUrl}>
              support
            </Link>{' '}
            for assistance.
          </StyledHelperText>
          <Label error={!!errors?.name} htmlFor="name" required>
            Workspace Name
          </Label>
          <ForwardedInput
            data-test-id="9a55c792-ca27-4451-a573-3d54f90a8a95"
            error={!!errors?.name}
            fullWidth
            helperText={errors?.name?.message}
            id="name"
            name="name"
            ref={register({ required: 'A workspace name is required' })}
            required
          />

          <Label error={!!errors?.nickname} htmlFor="nickname">
            Workspace Initials
          </Label>
          <ForwardedInput
            data-test-id="11d60ab1-ea80-431a-8770-7ef247865653"
            error={!!errors?.nickname}
            fullWidth
            helperText={errors?.nickname?.message}
            id="nickname"
            maxLength="5"
            name="nickname"
            ref={register}
          />

          <Label htmlFor="phoneNumber">Phone Number</Label>
          <ForwardedInput
            data-test-id="11547ada-4b8e-41a1-8a73-1685539b6963"
            disabled
            fullWidth
            id="phoneNumber"
            name="phoneNumber"
            ref={register}
          />

          <Label htmlFor="timezone" required>
            Time Zone
          </Label>
          <FormSelect
            control={control}
            data-test-id="5301f7e7-9826-45bf-9af7-422081308071"
            fullWidth
            id="timezone"
            isDisabled={disabled}
            name="timezone"
            options={timezoneOptions}
          />

          <StyledObservesDaylightSavingsTimeRow fullWidth>
            <ToggleSwitch
              data-test-id="04f60e67-7085-41f3-a954-716457733385"
              enabled={watchObservesDaylightSavings}
              id="observesDaylightSavings"
              name="observesDaylightSavings"
              onChange={() => {
                setValue(
                  'observesDaylightSavings',
                  !watchObservesDaylightSavings,
                  { shouldDirty: true },
                );
              }}
              ref={register({ name: 'observesDaylightSavings' })}
            />
            <StyledObservesDaylightSavingsTimeLabel>
              Observes Daylight Savings Time
            </StyledObservesDaylightSavingsTimeLabel>
          </StyledObservesDaylightSavingsTimeRow>

          <Label error={!!errors?.email} htmlFor="email" required>
            Email
          </Label>
          <ForwardedInput
            data-test-id="f27c5041-73e7-4192-b840-c9e784fdd64b"
            error={!!errors?.email}
            fullWidth
            helperText={
              errors?.email?.message || (emailDomain ? `@${emailDomain}` : '')
            }
            id="email"
            name="email"
            ref={register({ required: 'An email address is required' })}
            required
          />

          <Label error={!!errors?.forwardTo} htmlFor="forwardTo">
            Call Forwarding
          </Label>
          <FormMaskedInput
            control={control}
            data-test-id="66f93e16-ca0e-4694-a12c-9a2533c363b6"
            error={!!errors?.forwardTo}
            fullWidth
            helperText={errors?.forwardTo?.message}
            id="forwardTo"
            maskFn={telephoneInputMask}
            name="forwardTo"
          />

          <Label htmlFor="teamMemberIDs">Assigned Members</Label>
          <FormSelect
            control={control}
            data-test-id="3c1bdd09-dea3-4c6f-8a52-045b63a685ab"
            fullWidth
            id="teamMemberIDs"
            isDisabled={disabled}
            isMulti
            name="teamMemberIDs"
            options={teamMemberOptions}
          />
          <Link
            sx={{ mt: 1 }}
            data-test-id="3890a7e6-d117-4d4a-8add-8749f2b1efbb"
            href={`/boards/${boardId}/settings/general`}
          >
            Edit Workspace Business Hours
          </Link>
        </Form>
      </DrawerBody>
      <DrawerFooter>
        <Button
          data-test-id="052bbd39-9a08-465e-a9ba-0966bdb0c7a4"
          disabled={!formIsDirty}
          fetching={disabled}
          minWidth={90}
          onClick={handleSubmit(onSubmit)}
          size="sm"
          variant="primary"
        >
          Save
        </Button>
      </DrawerFooter>
    </>
  );
};

EditWorkspaceForm.propTypes = {
  boardId: PropTypes.string.isRequired,
  form: PropTypes.any, // eslint-disable-line
  onClose: PropTypes.func,
};

EditWorkspaceForm.defaultProps = {
  form: {},
  onClose: () => {},
};

export default EditWorkspaceForm;
